import React, { useCallback, useState, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import SimpleAccordion from '../../../ui_reusable/SimpleAccordion'
import SelectableDriverList from '../../../comp_reusable/lists/SelectableDriverList'
import {
  ImpactArtifactRequest,
  OptimizeArtifactRequest,
  ProjectSolutionDriver,
} from '@/api/generated/models'
import { StashedDriver } from './types/StashedDriver'

/** Drivers shown per page in the Select drivers grid. */
export const DRIVER_PAGE_SIZE = 10

interface DriversFormProps {
  projectSolutionDrivers: ProjectSolutionDriver[]
  solutionName?: string
  stashedDrivers: StashedDriver[]
  setStashedDrivers: React.Dispatch<React.SetStateAction<StashedDriver[]>>
  /** Server-side pagination (caller owns fetch). */
  totalAvailable?: number
  serverPage?: number
  loading?: boolean
  onPageChange?: (page: number) => void
  onSearchChange?: (term: string) => void
  onSortChange?: (
    sort: { key: string; direction: 'asc' | 'desc' } | null,
  ) => void
  onSelectAllToggle?: () => void
  allSelected?: boolean
  showSelectedOnly?: boolean
  onShowSelectedChange?: (showSelectedOnly: boolean) => void
  availableFirst?: boolean
  onAvailableFirstChange?: (availableFirst: boolean) => void
}

const DriversForm = ({
  projectSolutionDrivers,
  solutionName,
  stashedDrivers,
  setStashedDrivers,
  totalAvailable,
  serverPage = 1,
  loading = false,
  onPageChange,
  onSearchChange,
  onSortChange,
  onSelectAllToggle,
  allSelected,
  showSelectedOnly,
  onShowSelectedChange,
  availableFirst,
  onAvailableFirstChange,
}: DriversFormProps) => {
  const methods = useFormContext<
    OptimizeArtifactRequest | ImpactArtifactRequest
  >()

  const dependentDriverId = useWatch({
    control: methods.control,
    name: 'dependent_driver_id',
  })

  const independentDriversIds = useWatch({
    control: methods.control,
    name: 'independent_driver_ids',
  })

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const handleExpand = useCallback((dataIdx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(dataIdx)) next.delete(dataIdx)
      else next.add(dataIdx)
      return next
    })
  }, [])

  const handleDriverChange = (updatedSelectedDrivers: number[]) => {
    // Clear stashed drivers when user manually interacts with checkboxes
    // This restores the original behavior where "Was unselected!" is a temporary indicator
    setStashedDrivers([])
    methods.setValue('independent_driver_ids', updatedSelectedDrivers, {
      shouldValidate: true,
    })
  }

  useEffect(() => {
    // Only require independent_driver_ids for IMPACT solution
    const isImpact = solutionName === 'IMPACT'

    methods.register('independent_driver_ids', {
      validate: (value) => {
        if (!isImpact) return true // No validation for OPTIMIZE
        return (
          (value && value.length > 0) ||
          'At least one explanatory driver must be selected'
        )
      },
    })

    return () => {
      methods.unregister('independent_driver_ids', { keepValue: true })
    }
    /* eslint-disable-next-line */
  }, [solutionName])

  const serverSide = Boolean(onPageChange)

  return (
    <SimpleAccordion.Panel header="Select drivers" eventKey={'4'}>
      <SelectableDriverList
        data={projectSolutionDrivers}
        loading={loading}
        selectedDrivers={independentDriversIds || []}
        dependentDriverId={dependentDriverId}
        expandedRows={expandedRows}
        onExpandRow={handleExpand}
        totalAvailable={
          serverSide
            ? (totalAvailable ?? 0)
            : projectSolutionDrivers.length
        }
        totalSelected={independentDriversIds?.length}
        onSelectedChange={handleDriverChange}
        stashedDrivers={stashedDrivers}
        useRole={true}
        pageSize={DRIVER_PAGE_SIZE}
        serverSide={serverSide}
        serverPage={serverPage}
        onPageChange={onPageChange}
        onSearchChange={onSearchChange}
        onSortChange={onSortChange}
        onSelectAllToggle={onSelectAllToggle}
        allSelected={allSelected}
        showSelectedOnly={showSelectedOnly}
        onShowSelectedChange={onShowSelectedChange}
        availableFirst={availableFirst}
        onAvailableFirstChange={onAvailableFirstChange}
      />
    </SimpleAccordion.Panel>
  )
}

export default DriversForm
